/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Nucleus Platform API
 * Headless, multi-tenant commerce backend for jewelry brands.
 *
 * ## Surfaces
 *
 * | Prefix | Caller | Credential |
 * |---|---|---|
 * | `/platform/v1` | Platform operator | Platform token from **Platform: Auth** |
 * | `/admin/v1` | Staff of one organization | Staff token from **Admin: Auth** |
 * | `/storefront/v1` | A storefront | Store API key from **Admin: API keys** |
 * | `/public/v1` | Anyone (the brand application form) | None; rate limited per IP |
 *
 * Every credential is sent as `Authorization: Bearer <value>`. Use the **Authorize** button once and it applies to every request you try here.
 *
 * ## Conventions
 *
 * - **Ids** are UUID v7 strings. **Timestamps** are ISO 8601 in UTC. **Money** is `{ amount, currency }` with `amount` in integer minor units.
 * - **Lists** paginate by cursor: `?limit=20&cursor=...` in, `{ items, nextCursor }` out, `nextCursor` is `null` on the last page.
 * - **Errors** always use one envelope, `{ error: { code, message, requestId, details?, meta? } }`. Switch on `code`, never on `message`.
 * - **Request ids**: every response carries `x-request-id`. Quote it when reporting a problem.
 * - **Idempotency**: endpoints that create money-related state require an `Idempotency-Key` header; the header is listed on those endpoints.
 * - A record that belongs to another tenant answers **404**, never 403, so existence is not leaked.
 * OpenAPI spec version: 0.1.0
 */
import type {
  DeletePlatformV1MeToursParams,
  ForgotPasswordRequest,
  GoogleCallbackRequest,
  GoogleStart,
  GoogleStartRequest,
  MagicLinkRequest,
  MagicLinkVerifyRequest,
  PlatformLoginRequest,
  PlatformProfile,
  PostPlatformV1AuthLogoutBody,
  PostPlatformV1AuthRefreshBody,
  PutPlatformV1MeToursTourIdBody,
  PutPlatformV1MeToursTourIdPathParameters,
  ResetPasswordRequest,
  SignInProviders,
  TokenResponse,
  TourProgress,
  TourProgressList,
  TwoFactorChallenge,
  TwoFactorCodeRequest,
  TwoFactorDisableRequest,
  TwoFactorEnabled,
  TwoFactorSetup,
  TwoFactorStatus,
  TwoFactorVerifyRequest
} from '../model';

import { customInstance } from '../../../transport/mutator';
import type { BodyType } from '../../../transport/mutator';


type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];


  /**
 * Send the challenge token with a 6-digit authenticator code or one of the recovery codes. Answers like login; a recovery code is struck out once used. 401 UNAUTHORIZED for a wrong code, 401 TOKEN_EXPIRED once the challenge's expiresAt has passed (sign in again), 401 TOKEN_INVALID for a tampered or foreign challenge. Wrong codes count against the login limit of 10 per minute per IP, which answers 429 with Retry-After
 * @summary Finish a login that answered 202 with a two-factor challenge
 */
export const postPlatformV1Auth2faVerify = (
    twoFactorVerifyRequest: BodyType<TwoFactorVerifyRequest>,
 options?: SecondParameter<typeof customInstance<TokenResponse>>,) => {
      return customInstance<TokenResponse>(
      {url: `/platform/v1/auth/2fa/verify`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: twoFactorVerifyRequest
    },
      options);
    }
  /**
 * @summary Two-factor status of the signed-in account
 */
export const getPlatformV1Auth2fa = (

 options?: SecondParameter<typeof customInstance<TwoFactorStatus>>,) => {
      return customInstance<TwoFactorStatus>(
      {url: `/platform/v1/auth/2fa`, method: 'GET'
    },
      options);
    }
  /**
 * The secret is pending until /auth/2fa/enable confirms a code from the app. 409 while two-factor is already enabled
 * @summary Start enabling two-factor: a new secret and its otpauth URL for the authenticator app
 */
export const postPlatformV1Auth2faSetup = (

 options?: SecondParameter<typeof customInstance<TwoFactorSetup>>,) => {
      return customInstance<TwoFactorSetup>(
      {url: `/platform/v1/auth/2fa/setup`, method: 'POST'
    },
      options);
    }
  /**
 * Answers with ten recovery codes shown once; Nucleus keeps only their hashes
 * @summary Confirm the authenticator and switch two-factor on
 */
export const postPlatformV1Auth2faEnable = (
    twoFactorCodeRequest: BodyType<TwoFactorCodeRequest>,
 options?: SecondParameter<typeof customInstance<TwoFactorEnabled>>,) => {
      return customInstance<TwoFactorEnabled>(
      {url: `/platform/v1/auth/2fa/enable`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: twoFactorCodeRequest
    },
      options);
    }
  /**
 * @summary Replace the recovery codes
 */
export const postPlatformV1Auth2faRecoveryCodes = (
    twoFactorCodeRequest: BodyType<TwoFactorCodeRequest>,
 options?: SecondParameter<typeof customInstance<TwoFactorEnabled>>,) => {
      return customInstance<TwoFactorEnabled>(
      {url: `/platform/v1/auth/2fa/recovery-codes`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: twoFactorCodeRequest
    },
      options);
    }
  /**
 * 409 TWO_FACTOR_REQUIRED_BY_ORGANIZATION while an organization the person belongs to (or the platform, for operators) requires two-factor; meta.organizations names them
 * @summary Switch two-factor off with the password and a live code
 */
export const postPlatformV1Auth2faDisable = (
    twoFactorDisableRequest: BodyType<TwoFactorDisableRequest>,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/platform/v1/auth/2fa/disable`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: twoFactorDisableRequest
    },
      options);
    }
  /**
 * Always 202. The link points at the app's /magic-link page with ?token= (staff: plus &organization= when organizationSlug was given); single use, 15 minutes
 * @summary Email a one-time sign-in link
 */
export const postPlatformV1AuthMagicLink = (
    magicLinkRequest: BodyType<MagicLinkRequest>,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/platform/v1/auth/magic-link`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: magicLinkRequest
    },
      options);
    }
  /**
 * Answers like login: 200 with tokens, or 202 with a two-factor challenge when the account has two-factor on
 * @summary Exchange a magic link token for a session
 */
export const postPlatformV1AuthMagicLinkVerify = (
    magicLinkVerifyRequest: BodyType<MagicLinkVerifyRequest>,
 options?: SecondParameter<typeof customInstance<TokenResponse | TwoFactorChallenge>>,) => {
      return customInstance<TokenResponse | TwoFactorChallenge>(
      {url: `/platform/v1/auth/magic-link/verify`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: magicLinkVerifyRequest
    },
      options);
    }
  /**
 * @summary Which sign-in methods this surface offers, for the sign-in page
 */
export const getPlatformV1AuthProviders = (

 options?: SecondParameter<typeof customInstance<SignInProviders>>,) => {
      return customInstance<SignInProviders>(
      {url: `/platform/v1/auth/providers`, method: 'GET'
    },
      options);
    }
  /**
 * Answers the Google authorization URL with a signed state. Google returns the person to the app's /auth/google/callback page, which posts code and state to /auth/google/callback. 409 GOOGLE_SIGN_IN_UNAVAILABLE when the platform has no Google client
 * @summary Where to send the browser to sign in with Google
 */
export const postPlatformV1AuthGoogleStart = (
    googleStartRequest: BodyType<GoogleStartRequest>,
 options?: SecondParameter<typeof customInstance<GoogleStart>>,) => {
      return customInstance<GoogleStart>(
      {url: `/platform/v1/auth/google/start`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: googleStartRequest
    },
      options);
    }
  /**
 * Answers like login: 200 with tokens and the session cookie, or 202 with a two-factor challenge. 403 when no account uses the Google email (accounts come from invitations, never from Google), 401 TOKEN_INVALID when Google refused the code, 422 for a bad or expired state
 * @summary Finish Google sign-in
 */
export const postPlatformV1AuthGoogleCallback = (
    googleCallbackRequest: BodyType<GoogleCallbackRequest>,
 options?: SecondParameter<typeof customInstance<TokenResponse | TwoFactorChallenge>>,) => {
      return customInstance<TokenResponse | TwoFactorChallenge>(
      {url: `/platform/v1/auth/google/callback`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: googleCallbackRequest
    },
      options);
    }
  /**
 * For API clients that received `refreshToken` at login (requests without the X-Nucleus-Client header). The presented token is revoked and a new one issued; a token that was already rotated or revoked answers 401. Browser apps keep using the cookie and never call this
 * @summary Exchange a refresh token for a new access token and refresh token
 */
export const postPlatformV1AuthRefresh = (
    postPlatformV1AuthRefreshBody: BodyType<PostPlatformV1AuthRefreshBody>,
 options?: SecondParameter<typeof customInstance<TokenResponse>>,) => {
      return customInstance<TokenResponse>(
      {url: `/platform/v1/auth/refresh`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postPlatformV1AuthRefreshBody
    },
      options);
    }
  /**
 * Returns a Bearer token for API clients and sets the `nucleus_platform` HttpOnly session cookie for the platform app. Answers 202 with a challenge when the account has two-factor on; finish at /auth/2fa/verify
 * @summary Platform admin login
 */
export const postPlatformV1AuthLogin = (
    platformLoginRequest: BodyType<PlatformLoginRequest>,
 options?: SecondParameter<typeof customInstance<TokenResponse | TwoFactorChallenge>>,) => {
      return customInstance<TokenResponse | TwoFactorChallenge>(
      {url: `/platform/v1/auth/login`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: platformLoginRequest
    },
      options);
    }
  /**
 * Revokes the session behind the `nucleus_platform` cookie and clears it; an API client passes its refresh token in the body instead. Safe to call without either
 * @summary End the platform browser session
 */
export const postPlatformV1AuthLogout = (
    postPlatformV1AuthLogoutBody: BodyType<PostPlatformV1AuthLogoutBody>,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/platform/v1/auth/logout`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postPlatformV1AuthLogoutBody
    },
      options);
    }
  /**
 * @summary Current platform user and role
 */
export const getPlatformV1Me = (

 options?: SecondParameter<typeof customInstance<PlatformProfile>>,) => {
      return customInstance<PlatformProfile>(
      {url: `/platform/v1/me`, method: 'GET'
    },
      options);
    }
  /**
 * Always answers 202; the link works once and expires in 15 minutes
 * @summary Email a password reset link to a platform user
 */
export const postPlatformV1AuthPasswordForgot = (
    forgotPasswordRequest: BodyType<ForgotPasswordRequest>,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/platform/v1/auth/password/forgot`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: forgotPasswordRequest
    },
      options);
    }
  /**
 * Ends every existing session of the account
 * @summary Set a new password with a reset or set-password link
 */
export const postPlatformV1AuthPasswordReset = (
    resetPasswordRequest: BodyType<ResetPasswordRequest>,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/platform/v1/auth/password/reset`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: resetPasswordRequest
    },
      options);
    }
  /**
 * @summary Guided-tour progress of the signed-in operator
 */
export const getPlatformV1MeTours = (

 options?: SecondParameter<typeof customInstance<TourProgressList>>,) => {
      return customInstance<TourProgressList>(
      {url: `/platform/v1/me/tours`, method: 'GET'
    },
      options);
    }
  /**
 * Without tourId every tour is offered again; with it only that one
 * @summary Reset guided-tour progress
 */
export const deletePlatformV1MeTours = (
    params?: DeletePlatformV1MeToursParams,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/platform/v1/me/tours`, method: 'DELETE',
        params
    },
      options);
    }
  /**
 * Upsert on the tour id: the latest version and status win. At most 300 tours per person
 * @summary Record a tour as completed or dismissed
 */
export const putPlatformV1MeToursTourId = (
    { tourId }: PutPlatformV1MeToursTourIdPathParameters,
    putPlatformV1MeToursTourIdBody: BodyType<PutPlatformV1MeToursTourIdBody>,
 options?: SecondParameter<typeof customInstance<TourProgress>>,) => {
      return customInstance<TourProgress>(
      {url: `/platform/v1/me/tours/${tourId}`, method: 'PUT',
      headers: {'Content-Type': 'application/json', },
      data: putPlatformV1MeToursTourIdBody
    },
      options);
    }
  export type PostPlatformV1Auth2faVerifyResult = NonNullable<Awaited<ReturnType<typeof postPlatformV1Auth2faVerify>>>
export type GetPlatformV1Auth2faResult = NonNullable<Awaited<ReturnType<typeof getPlatformV1Auth2fa>>>
export type PostPlatformV1Auth2faSetupResult = NonNullable<Awaited<ReturnType<typeof postPlatformV1Auth2faSetup>>>
export type PostPlatformV1Auth2faEnableResult = NonNullable<Awaited<ReturnType<typeof postPlatformV1Auth2faEnable>>>
export type PostPlatformV1Auth2faRecoveryCodesResult = NonNullable<Awaited<ReturnType<typeof postPlatformV1Auth2faRecoveryCodes>>>
export type PostPlatformV1Auth2faDisableResult = NonNullable<Awaited<ReturnType<typeof postPlatformV1Auth2faDisable>>>
export type PostPlatformV1AuthMagicLinkResult = NonNullable<Awaited<ReturnType<typeof postPlatformV1AuthMagicLink>>>
export type PostPlatformV1AuthMagicLinkVerifyResult = NonNullable<Awaited<ReturnType<typeof postPlatformV1AuthMagicLinkVerify>>>
export type GetPlatformV1AuthProvidersResult = NonNullable<Awaited<ReturnType<typeof getPlatformV1AuthProviders>>>
export type PostPlatformV1AuthGoogleStartResult = NonNullable<Awaited<ReturnType<typeof postPlatformV1AuthGoogleStart>>>
export type PostPlatformV1AuthGoogleCallbackResult = NonNullable<Awaited<ReturnType<typeof postPlatformV1AuthGoogleCallback>>>
export type PostPlatformV1AuthRefreshResult = NonNullable<Awaited<ReturnType<typeof postPlatformV1AuthRefresh>>>
export type PostPlatformV1AuthLoginResult = NonNullable<Awaited<ReturnType<typeof postPlatformV1AuthLogin>>>
export type PostPlatformV1AuthLogoutResult = NonNullable<Awaited<ReturnType<typeof postPlatformV1AuthLogout>>>
export type GetPlatformV1MeResult = NonNullable<Awaited<ReturnType<typeof getPlatformV1Me>>>
export type PostPlatformV1AuthPasswordForgotResult = NonNullable<Awaited<ReturnType<typeof postPlatformV1AuthPasswordForgot>>>
export type PostPlatformV1AuthPasswordResetResult = NonNullable<Awaited<ReturnType<typeof postPlatformV1AuthPasswordReset>>>
export type GetPlatformV1MeToursResult = NonNullable<Awaited<ReturnType<typeof getPlatformV1MeTours>>>
export type DeletePlatformV1MeToursResult = NonNullable<Awaited<ReturnType<typeof deletePlatformV1MeTours>>>
export type PutPlatformV1MeToursTourIdResult = NonNullable<Awaited<ReturnType<typeof putPlatformV1MeToursTourId>>>
